import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type ContentLocalizationDTO,
	type ContentTextDTO,
	ContentTranslationOrigin,
	type ContentVersionDTO,
	ContentLocalizationStatus as S,
} from '@today/types'
import type { AddLanguageDialogViewProps, AddLanguageValuesView } from '@today/ui-domain'
import { useEffect, useState } from 'react'
import { contentService } from '@/services/content.service'
import {
	addLanguageBody,
	addLanguageErrors,
	type DraftValues,
	isDraftDirty,
	localizationUpdateBody,
	localizationUpdateErrors,
	orderedLocales,
	sourceChanged,
} from './localization-rules'
import { statusLabel } from './text-list-rules'
import { contentKeys, useContentT, useContentViewer, useNow } from './useContentQueries'

/** The "Other" choice of the add-language dialog (mirrors the view's constant). */
const OTHER = '__other'

/** The selected localization, its editable draft and the draft save (TO-420 content-admin-ui REQ-004). */
export function useDraft(textId: string, summaryId: string | undefined) {
	const { t, fieldError } = useContentT()
	const client = useQueryClient()
	const localization = useQuery({
		queryKey: contentKeys.localization(summaryId ?? ''),
		queryFn: () => contentService.getLocalization(summaryId as string),
		enabled: Boolean(summaryId),
	})
	const stored: DraftValues = { title: localization.data?.title ?? '', body: localization.data?.body ?? '' }
	const [draft, setDraft] = useState<DraftValues>(stored)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [formError, setFormError] = useState<string | null>(null)
	// A newly loaded (or reloaded) localization replaces the draft.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the stored content changes
	useEffect(() => {
		setDraft(stored)
		setErrors({})
		setFormError(null)
	}, [localization.data?.id, localization.data?.updatedAt])
	const save = useMutation({
		mutationFn: (body: Record<string, unknown>) => contentService.saveLocalization(summaryId as string, body),
		onSuccess: async (saved: ContentLocalizationDTO) => {
			client.setQueryData(contentKeys.localization(saved.id), saved)
			await client.invalidateQueries({ queryKey: contentKeys.versions(textId) })
			await client.invalidateQueries({ queryKey: contentKeys.text(textId) })
		},
		onError: (error) => {
			const { field, message } = fieldError(error)
			if (field === 'title' || field === 'body') setErrors({ [field]: message })
			else setFormError(message)
		},
	})
	return {
		localization: localization.data,
		draft,
		errors,
		formError,
		dirty: Boolean(localization.data) && isDraftDirty(draft, stored),
		isSaving: save.isPending,
		change: (field: keyof DraftValues, value: string) => setDraft((d) => ({ ...d, [field]: value })),
		reset: () => setDraft(stored),
		save: () => {
			setFormError(null)
			const body = localizationUpdateBody(draft)
			const found = localizationUpdateErrors(body)
			setErrors(Object.fromEntries(Object.entries(found).map(([f, key]) => [f, t(key)])))
			if (Object.keys(found).length === 0) save.mutate(body)
		},
	}
}

const EMPTY_ADD: AddLanguageValuesView = {
	locale: '',
	otherLocale: '',
	origin: ContentTranslationOrigin.MANUAL,
	translatedFromId: '',
}

/** The POST body (title/body copied from the source localization) and its translated schema errors. */
function addLanguageRequest(
	locale: string,
	values: AddLanguageValuesView,
	from: ContentLocalizationDTO | undefined,
	t: (key: string) => string,
) {
	const body = addLanguageBody({
		locale,
		origin: values.origin,
		translatedFromId: values.translatedFromId,
		title: from?.title ?? '',
		body: from?.body ?? '',
	})
	const found = addLanguageErrors(body)
	return { body, errors: Object.fromEntries(Object.entries(found).map(([f, key]) => [f, t(key)])) }
}

/** POST the new localization; close, reload and select it on 201; map refusals to the dialog. */
function useAddMutation(
	textId: string,
	versionId: string,
	onAdded: (locale: string) => void,
	set: {
		setOpen: (open: boolean) => void
		setErrors: (errors: Record<string, string>) => void
		setFormError: (message: string | null) => void
	},
) {
	const { fieldError } = useContentT()
	const client = useQueryClient()
	return useMutation({
		mutationFn: (body: Record<string, unknown>) => contentService.addLocalization(versionId, body),
		onSuccess: async (created: ContentLocalizationDTO) => {
			set.setOpen(false)
			await client.invalidateQueries({ queryKey: contentKeys.versions(textId) })
			await client.invalidateQueries({ queryKey: contentKeys.text(textId) })
			onAdded(created.locale)
		},
		onError: (error) => {
			const { field, message } = fieldError(error)
			if (field === 'locale') set.setErrors({ locale: message })
			else set.setFormError(message)
		},
	})
}

/** "Add language" on a version: dialog, schema check, POST, then select the new tab. */
export function useAddLanguage(args: {
	text: ContentTextDTO
	version: ContentVersionDTO | undefined
	locales: readonly string[]
	onAdded: (locale: string) => void
}): AddLanguageDialogViewProps & { open: () => void } {
	const { text, version } = args
	const { t } = useContentT()
	const [isOpen, setOpen] = useState(false)
	const [values, setValues] = useState<AddLanguageValuesView>(EMPTY_ADD)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [formError, setFormError] = useState<string | null>(null)
	const present = new Set(version?.localizations.map((l) => l.locale))
	const available = args.locales.filter((l) => !present.has(l))
	const sourceId = version?.localizations.find((l) => l.locale === text.sourceLocale)?.id ?? ''
	const from = useQuery({
		queryKey: contentKeys.localization(values.translatedFromId),
		queryFn: () => contentService.getLocalization(values.translatedFromId),
		enabled: isOpen && Boolean(values.translatedFromId),
	})
	const add = useAddMutation(text.id, version?.id ?? '', args.onAdded, { setOpen, setErrors, setFormError })
	const locale = values.locale === OTHER ? values.otherLocale.trim() : values.locale
	return {
		t,
		isOpen,
		values,
		locales: available,
		sources: (version?.localizations ?? []).map((l) => ({ id: l.id, label: l.locale })),
		errors,
		formError,
		isBusy: add.isPending || from.isFetching,
		onChange: (field, value) => setValues((v) => ({ ...v, [field]: value })),
		onConfirm: () => {
			setFormError(null)
			const { body, errors: found } = addLanguageRequest(locale, values, from.data, t)
			setErrors(found.locale ? { locale: found.locale } : {})
			if (Object.keys(found).length === 0) add.mutate(body)
			else if (!found.locale) setFormError(t('validation.form'))
		},
		onCancel: () => setOpen(false),
		open: () => {
			setValues({ ...EMPTY_ADD, locale: available[0] ?? OTHER, translatedFromId: sourceId })
			setErrors({})
			setFormError(null)
			setOpen(true)
		},
	}
}

/** Tab entries of a version (source language first) and the selected tab's facts. */
export function useTabFacts(
	text: ContentTextDTO,
	version: ContentVersionDTO | undefined,
	versions: readonly ContentVersionDTO[],
	localization: ContentLocalizationDTO | undefined,
) {
	const now = useNow()
	const { caps } = useContentViewer()
	return {
		tabs: orderedLocales(version?.localizations ?? [], text.sourceLocale).map((l) => ({
			id: l.id,
			locale: l.locale,
			label: statusLabel(l, now),
		})),
		origin: localization?.translationOrigin ?? null,
		sourceChanged: localization ? sourceChanged(localization, versions, text.sourceLocale) : false,
		editable: caps.write && localization?.status === S.DRAFT,
		canAdd: caps.write && Boolean(version),
	}
}
